import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Upload, Loader2 } from "lucide-react";
import { compressImage, fileToDataUrl } from "@/lib/imageUtils";

// 身分証明書（表面/裏面）撮影用。免許証イラストのガイド枠付き（カード縦横比 85.6:54）。
// side="front": 表面（帯・顔写真シルエット・数字枠） / side="back": 裏面（破線メモ欄）
export function IdCapture({ label, onChange, testid, side = "front" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [camOn, setCamOn] = useState(false);
  const [starting, setStarting] = useState(false);
  const [shot, setShot] = useState(null);
  const [err, setErr] = useState(null);

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  };

  useEffect(() => {
    if (camOn && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [camOn]);

  useEffect(() => stopCam, []);

  const start = async () => {
    setErr(null);
    setStarting(true);
    try {
      // 身分証撮影は背面カメラ優先
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = s;
      setCamOn(true);
    } catch {
      setErr("カメラにアクセスできません。ブラウザのカメラ許可をご確認いただくか、画像ファイルを選択してください。");
    } finally {
      setStarting(false);
    }
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v) return;
    const vw = v.videoWidth || 1280;
    const vh = v.videoHeight || 720;
    // ガイド枠の内側だけを切り出す（枠の外側は破棄）
    const fw = 0.86 * vw;
    const fh = fw / 1.586;
    const fx = (vw - fw) / 2;
    const fy = Math.max(0, (vh - fh) / 2);
    const c = document.createElement("canvas");
    c.width = Math.round(fw);
    c.height = Math.round(fh);
    c.getContext("2d").drawImage(v, fx, fy, fw, fh, 0, 0, c.width, c.height);
    const url = await compressImage(c.toDataURL("image/jpeg", 0.9));
    setShot(url);
    onChange(url);
    stopCam();
  };

  const retake = () => {
    setShot(null);
    onChange(null);
    start();
  };

  const fromFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = await compressImage(await fileToDataUrl(f));
    setShot(url);
    onChange(url);
    e.target.value = "";
  };

  return (
    <div>
      <span className="block text-xs text-white/50 mb-1.5">{label}</span>
      {shot ? (
        <div className="flex items-end gap-4">
          <img src={shot} alt={label} className="w-64 max-w-full rounded-lg border border-white/15" data-testid={`${testid}-preview`} />
          <button
            type="button"
            onClick={retake}
            data-testid={`${testid}-retake`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-xs text-white/70 hover:text-gold hover:border-gold/40 transition-colors"
          >
            <RefreshCw size={13} /> 撮り直す
          </button>
        </div>
      ) : camOn ? (
        <div className="space-y-3">
          <div className="relative w-full max-w-md">
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg border border-white/15 bg-black" data-testid={`${testid}-video`} />
            {/* ガイド枠（免許証イラスト） */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative w-[86%]" style={{ aspectRatio: "1.586", boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)" }}>
                {side === "front" ? (
                  <svg viewBox="0 0 856 540" className="absolute inset-0 h-full w-full" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="3" data-testid={`${testid}-frame`}>
                    <rect x="10" y="10" width="836" height="520" rx="26" />
                    <rect x="40" y="38" width="776" height="58" rx="10" />
                    <rect x="40" y="112" width="776" height="44" rx="10" />
                    <rect x="40" y="172" width="540" height="44" rx="10" />
                    <rect x="612" y="172" width="204" height="252" rx="10" />
                    <circle cx="714" cy="266" r="44" />
                    <path d="M652 402 C652 322 776 322 776 402" />
                    <rect x="40" y="380" width="26" height="30" />
                    <rect x="40" y="420" width="26" height="30" />
                    <rect x="40" y="460" width="26" height="30" />
                    {[...Array(8)].map((_, i) => (
                      <rect key={i} x={170 + i * 40} y={436} width={30} height={38} rx={4} />
                    ))}
                  </svg>
                ) : (
                  <svg viewBox="0 0 856 540" className="absolute inset-0 h-full w-full" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="3" data-testid={`${testid}-frame`}>
                    <rect x="10" y="10" width="836" height="520" rx="26" />
                    <rect x="40" y="36" width="776" height="190" rx="12" />
                    <rect x="40" y="36" width="100" height="38" rx="12" />
                    {[96, 130, 164, 198].map((y) => (
                      <line key={y} x1="58" y1={y} x2="798" y2={y} strokeDasharray="10 8" />
                    ))}
                    <line x1="380" y1="452" x2="816" y2="452" />
                    <line x1="380" y1="494" x2="816" y2="494" />
                  </svg>
                )}
              </div>
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded bg-black/50 px-2 py-1 text-[10px] text-white/90 whitespace-nowrap">
                {side === "front" ? "表面を枠に合わせてください" : "裏面を枠に合わせてください"}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={capture}
              data-testid={`${testid}-capture`}
              className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors"
            >
              <Camera size={15} /> 撮影する
            </button>
            <button type="button" onClick={stopCam} className="rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors">
              キャンセル
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={start}
              disabled={starting}
              data-testid={`${testid}-start`}
              className="inline-flex items-center gap-2 rounded-lg border border-gold/40 text-gold px-4 py-2 text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
            >
              {starting ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} カメラで撮影（ガイド枠付き）
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid={`${testid}-upload`}
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <Upload size={15} /> 画像ファイルを選択
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`${testid}-file`} onChange={fromFile} />
          </div>
          <p className="mt-2 text-xs text-white/30">運転免許証・マイナンバーカード等。表面と裏面の両方が必要です（画像は2MB以下に自動圧縮されます）。</p>
        </div>
      )}
      {err && <p className="mt-2 text-xs text-red-300" data-testid={`${testid}-error`}>{err}</p>}
    </div>
  );
}
